// src/store/index.js
import axiosInstance from "@/axiosInstance";
import { createStore } from "vuex";

const store = createStore({
  state: {
    currentUser: null,
    clients: [],
    operators: [],
    items: [],
    orders: [],
    ordersPagination: {},
    quotes: [],
    pagination: { next: null, previous: null },
  },
  getters: {
    "items/list/filtered": (state) => (category) =>
      (state.items || []).filter((item) => item.category === category) ?? [],
    "items/list": (state) => state.items ?? [],
    "items/pagination": (state) => state.pagination,
    "clients/list": (state) => state.clients ?? [],
    "clients/pagination": (state) => state.pagination,
    "operators/list": (state) => state.operators ?? [],
    "quotes/list": (state) => state.quotes ?? [],
    "quotes/pagination": (state) => state.pagination,
    "orders/list": (state) => state.orders ?? [],
    "orders/pagination": (state) => state.ordersPagination,
  },
  mutations: {
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    CLEAR_CURRENT_USER(state) {
      state.currentUser = null;
    },

    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    // Nuova mutation per aggiungere al volo un cliente
    ADD_CLIENT(state, newClient) {
      state.clients.push(newClient);
    },
    // Nuova mutation per rimuovere un cliente
    REMOVE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((client) => client.id !== clientId);
    },
    // Nuova mutaion per aggiornare un cliente esistente
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex(
        (client) => client.id === updatedClient.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.clients.splice(index, 1, updatedClient);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OPERATORS(state, operators) {
      state.operators = operators;
    },
    // Nuova mutation per aggiungere al volo un operatore
    ADD_OPERATOR(state, newOperator) {
      state.operators.push(newOperator);
    },
    // Nuova mutation per rimuovere un operatore
    REMOVE_OPERATOR(state, operatorId) {
      state.operators = state.operators.filter((op) => op.id !== operatorId);
    },
    // Nuova mutaion per aggiornare un operatore esistente
    UPDATE_OPERATOR(state, updatedOperator) {
      const index = state.operators.findIndex(
        (op) => op.id === updatedOperator.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.operators.splice(index, 1, updatedOperator);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ITEMS(state, items) {
      state.items = items;
    },
    // Nuova mutation per la gestione della paginazione della lista degli articoli
    // SET_PAGINATION(state, pageInfo) {
    //   state.pagination = pageInfo;
    // },

    SET_PAGINATION(state, pageInfo) {
      state.pagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem: (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem: Math.min(
          pageInfo.currentPage * pageInfo.itemsPerPage,
          pageInfo.totalItems
        ),
      };
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_ITEM(state, newItem) {
      state.items.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.items.splice(index, 1, updatedItem);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ORDERS(state, orders) {
      state.orders = orders;
    },
    SET_ORDERS_PAGINATION(state, pageInfo) {
      state.ordersPagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem:
          pageInfo.startItem ??
          (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem:
          pageInfo.endItem ??
          Math.min(
            pageInfo.currentPage * pageInfo.itemsPerPage,
            pageInfo.totalItems
          ),
      };
    },

    // ---------------------------------------------------------------------------//

    SET_QUOTES(state, quotes) {
      state.quotes = quotes;
    },
    // Nuova mutation per aggiungere al volo un quote
    ADD_QUOTE(state, newQuote) {
      state.quotes.push(newQuote);
    },
    // Nuova mutation per rimuovere un quote
    REMOVE_QUOTE(state, quoteId) {
      state.quotes = state.quotes.filter((quote) => quote.id !== quoteId);
    },
    // Nuova mutaion per aggiornare un quote esistente
    UPDATE_QUOTE(state, updatedQuote) {
      const index = state.quotes.findIndex(
        (quote) => quote.id === updatedQuote.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.quotes.splice(index, 1, updatedQuote);
      }
    },
    // Per aggiungere documenti al bucket
    ADD_DOC_QUOTE(state, { quoteId, document }) {
      const index = state.quotes.findIndex((q) => q.id === quoteId);
      if (index === -1) return;

      // se non c’è ancora l’array, lo creo
      if (!Array.isArray(state.quotes[index].documents)) {
        // Vue 3: ok aggiungere una nuova proprietà reattiva
        state.quotes[index].documents = [];
      }

      state.quotes[index].documents.push(document);
    },
  },

  actions: {
    async fetchClients({ commit }, { page = 1, limit, search = "" } = {}) {
      try {
        const { data } = await axiosInstance.get("/client/get-all", {
          params: { page, limit, ...(search ? { search } : {}) },
        });
        commit("SET_CLIENTS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun cliente”: trasformalo in array vuoto
          commit("SET_CLIENTS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        console.error("fetchClients failed:", err);
        throw err; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per creare un cliente
    async createClient({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/client/add", payload);

        // commit("ADD_CLIENT", response.data.operator);
        await dispatch("fetchClients");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo client:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un cliente esistente
    async updateClient({ commit }, { clientId, payload }) {
      const response = await axiosInstance.patch(
        `client/update/${clientId}`,
        payload
      );
      commit("UPDATE_CLIENT", response.data.client);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) il cliente
    async archiveClient({ commit }, clientId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`client/archive/${clientId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_CLIENT", clientId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione del cliente: ", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOperators({ commit }) {
      try {
        const response = await axiosInstance.get("/auth/get-all"); // endpoint della tua API
        commit("SET_OPERATORS", response.data);
      } catch (error) {
        console.error("Errore durante il fetch degli operators:", error);
        throw error;
      }
    },

    // Nuova azione per creare un operatore
    async createOperator({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post(
          "auth/create-operator",
          payload
        );

        // commit("ADD_OPERATOR", response.data.operator);
        await dispatch("fetchOperators");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo operator:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un operatore esistente
    async updateOperator({ commit }, { operatorId, payload }) {
      const response = await axiosInstance.patch(
        `auth/update/${operatorId}`,
        payload
      );
      commit("UPDATE_OPERATOR", response.data.operator);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’operatore
    async archiveOperator({ commit }, operatorId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`auth/archive/${operatorId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OPERATOR", operatorId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un operator:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchItems(
      { commit },
      {
        page = 1,
        limit,
        category = null,
        subCategory = "",
        search = "",
        itemCode = "",
      } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit,
            ...(category ? { category } : {}),
            ...(subCategory ? { subCategory } : {}),
            ...(search ? { search } : {}),
            ...(itemCode ? { itemCode } : {}),
          },
        });
        commit("SET_ITEMS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          // first: data.first ?? null,
          // next: data.next ?? null,
          // previous: data.previous ?? null,
          // last: data.last ?? null,
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (error) {
        if (error.response?.status === 404) {
          // il server dice “nessun articolo: trasformalo in array vuoto
          commit("SET_ITEMS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        // console.error("fetchClients failed:", error);
        throw error; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per fetchare gli articoli venduti ai clienti
    async fetchClientSoldItems(_, clientId) {
      try {
        const response = await axiosInstance.get(
          `client/${clientId}/client-info`
        );
        return response.data;
      } catch (error) {
        console.error(
          "Errore durante il caricamento degli articoli venduti associati al cliente",
          error
        );
        throw error;
      }
    },

    // Nuova azione per creare un item
    async createItem({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("item/create", payload);

        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchItems");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente
    async updateItem({ commit }, { itemId, payload }) {
      const response = await axiosInstance.patch(
        `item/update/${itemId}`,
        payload
      );
      commit("UPDATE_ITEM", response.data.item);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’item
    async archiveItem({ commit }, itemId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`item/archive/${itemId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_ITEM", itemId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOrders({ commit }, { page = 1, limit = 10, status } = {}) {
      try {
        const { data } = await axiosInstance.get("/order/get-all", {
          params: {
            page,
            limit,
            ...(status ? { status } : {}),
          },
        });
        commit("SET_ORDERS", Array.isArray(data.results) ? data.results : []);
        commit("SET_ORDERS_PAGINATION", data.pagination);
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          commit("SET_ORDERS", []);
          commit("SET_ORDERS_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: limit,
            startItem: 0,
            endItem: 0,
          });
          return [];
        }
        throw err;
      }
    },

    async createOrder(_, payload) {
      const response = await axiosInstance.post("order/create", payload);
      return response;
    },

    async updateOrder(_, { id, payload }) {
      const response = await axiosInstance.patch(`order/update/${id}`, payload);
      return response;
    },

    async archiveOrder(_, orderId) {
      const response = await axiosInstance.post(`order/archive/${orderId}`);
      return response;
    },

    async splitOrder(_, { id, confirmedQty, suspendedQty }) {
      const response = await axiosInstance.patch(`order/${id}/split`, {
        confirmedQty,
        suspendedQty,
      });
      return response;
    },

    async completeOrder(_, { id, damagedQty = 0 }) {
      const response = await axiosInstance.patch(`order/${id}/complete`, {
        damagedQty,
      });
      return response;
    },

    async fetchActiveOrders({ commit }) {
      const statuses = ["creato", "in_sospeso", "confermato"];
      const results = await Promise.all(
        statuses.map((status) =>
          axiosInstance
            .get("/order/get-all", { params: { status, limit: 10000 } })
            .then((r) => (Array.isArray(r.data.results) ? r.data.results : []))
            .catch((err) =>
              err.response?.status === 404 ? [] : Promise.reject(err)
            )
        )
      );
      const merged = results
        .flat()
        .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
      commit("SET_ORDERS", merged);
      commit("SET_ORDERS_PAGINATION", {
        currentPage: 1,
        totalPages: 1,
        totalItems: merged.length,
        itemsPerPage: merged.length,
        startItem: merged.length ? 1 : 0,
        endItem: merged.length,
      });
      return merged;
    },

    // ---------------------------------------------------------------------------//

    async fetchQuotes(
      { commit },
      { page = 1, limit = 7, search = "", archived, isConfirmed } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: {
            page,
            limit,
            ...(search ? { search } : {}),
            ...(archived !== undefined ? { archived } : {}), // se non è definito, non lo mando
            ...(isConfirmed !== undefined ? { isConfirmed } : {}),
          },
        });
        // console.log("fetchQuotes data", data);
        // console.log("allquotes", data);
        commit("SET_QUOTES", data.results);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun preventivo”: trasformalo in array vuoto
          commit("SET_QUOTES", []);
          throw err;
        }
        console.error("Errore durante il fetch dei preventivi:", err);
        throw err;
      }
    }, // quando verranno usate le rotte del backend

    // Nuova azione per creare un quote
    async createQuote({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("quote/create", payload);

        // commit("ADD_QUOTE", response.data.quote);
        await dispatch("fetchQuotes");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo preventivo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per caricare un documento di un quote
    async uploadQuoteDocument({ commit }, { quoteId, document, filename }) {
      try {
        const formData = new FormData();
        formData.append("document", document, filename);

        // POST multipart/form-data alla tua rotta
        // Nota: axiosInstance ha baseURL impostato su '/api'
        const response = await axiosInstance.post(
          `/upload/quotes/${quoteId}/documents`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        // Il backend restituisce il record creato sotto la chiave "doc"
        // (fallback su "document" per compatibilità). Commit solo se presente,
        // così non inseriamo valori vuoti nello store.
        const uploadedDoc = response.data?.doc || response.data?.document;
        if (uploadedDoc) {
          commit("ADD_DOC_QUOTE", {
            quoteId,
            document: uploadedDoc,
          });
        }
        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento:", error);
        throw error;
      }
    },

    async sendQuoteDocumentEmail(
      _context,
      { quoteId, objectName, contact = "" }
    ) {
      return await axiosInstance.post(
        `/upload/quotes/${quoteId}/documents/send-email`,
        { objectName, contact }
      );
    },

    // Nuova azione per aggiornare un quote esistente
    async updateQuote({ commit }, { quoteId, payload }) {
      const response = await axiosInstance.patch(
        `quote/update-with-delta/${quoteId}`,
        payload
      );
      commit("UPDATE_QUOTE", response.data.quote);
      return response;
    },

    // Nuova azione per verificare se ci sono preventivi archiviati
    async hasArchivedQuotes() {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: { page: 1, limit: 1, archived: true },
        });

        /* nuovo formato: { results: [ … ], pagination: { totalItems, … } } */
        return (data.pagination?.totalItems || 0) > 0;
      } catch (err) {
        console.error(
          "Errore durante il tentantivo di ritrovare i preventivi archiviati: ",
          err
        );
        return false; // fallback sicuro
      }
    },

    // ✅ archivia preventivo
    async archiveQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: true,
        });
        // rimuovo dalla lista corrente (in "attivi" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante l'archiviazione del preventivo:", error);
        throw error;
      }
    },

    // ✅ ripristina preventivo (nuova azione)
    async restoreQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: false,
        });
        // rimuovo dalla lista corrente (in "archiviati" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante il ripristino del preventivo:", error);
        throw error;
      }
    },
  },
});

export default store;
